import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Surface, Button, Badge, Tabs, Grid, Textarea } from '../../ui/components';
import { ArrowLeft, User, Home, DollarSign, CreditCard, Briefcase, Wallet, CircleCheck, Clock, AlertCircle, FileText, Send, Plus } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

// Mock application data
const mockApplication = {
  id: '1',
  anonymous_id: 'LK-A1B2C3D4',
  status: 'underwriting_in_progress',
  readiness_score: 82,
  
  applicant: {
    first_name: 'John',
    last_name: 'Smith',
    email: 'john.smith@example.com',
    phone: '(555) 123-4567',
  },
  
  selected_product: {
    name: 'Conventional Loan',
    short_name: 'Conventional',
    icon: '🏠',
  },
  
  property: {
    type: 'Single Family',
    use: 'Primary Residence',
    address: '123 Main Street, Austin, TX 78701',
    purchase_price: 550000,
    down_payment: 110000,
    loan_amount: 440000,
    ltv_ratio: 80,
  },
  
  credit: {
    score: 745,
    report_date: '2025-12-15',
    accounts: 12,
    derogatory: 0,
    inquiries: 2,
  },
  
  income: {
    monthly: 12500,
    annual: 150000,
    sources: [
      { type: 'W2 Employment', employer: 'Tech Corp Inc', amount: 10000, verified: true },
      { type: 'Bonus', employer: 'Tech Corp Inc', amount: 2500, verified: false },
    ],
    dti_ratio: 32.5,
  },
  
  assets: {
    total: 285000,
    items: [
      { type: 'Checking', institution: 'Chase Bank', amount: 45000, verified: true },
      { type: 'Savings', institution: 'Chase Bank', amount: 120000, verified: true },
      { type: '401k', institution: 'Fidelity', amount: 95000, verified: false },
      { type: 'Stocks', institution: 'Robinhood', amount: 25000, verified: false },
    ],
  },
  
  documents: [
    { id: 1, name: 'Pay Stubs (Last 2)', category: 'Income', status: 'verified' },
    { id: 2, name: 'W2 Forms (2 Years)', category: 'Income', status: 'verified' },
    { id: 3, name: 'Bank Statements (2 Months)', category: 'Assets', status: 'in_review' },
    { id: 4, name: 'Tax Returns (2 Years)', category: 'Income', status: 'pending' },
    { id: 5, name: 'Photo ID', category: 'Identity', status: 'verified' },
    { id: 6, name: 'Purchase Contract', category: 'Property', status: 'verified' },
  ],
  
  conditions: [
    { id: 1, title: 'Verify Employment', status: 'satisfied', priority: 1 },
    { id: 2, title: 'Explain Large Deposit', status: 'pending', priority: 1 },
    { id: 3, title: 'Updated Bank Statement', status: 'in_review', priority: 2 },
    { id: 4, title: 'HOA Certification', status: 'not_started', priority: 3 },
  ],
};

const getDocStatusIcon = (status) => {
  switch (status) {
    case 'verified':
      return <CircleCheck size={16} color="var(--lk-success-500)" />;
    case 'in_review':
      return <Clock size={16} color="var(--lk-primary-500)" />;
    case 'pending':
      return <AlertCircle size={16} color="var(--lk-warning-500)" />;
    default:
      return <FileText size={16} />;
  }
};

const getConditionStatusColor = (status) => {
  switch (status) {
    case 'satisfied': return 'success';
    case 'in_review': return 'primary';
    case 'pending': return 'warning';
    default: return 'neutral';
  }
};

const MetricCard = ({ label, value, color }) => (
  <Surface padding="var(--lk-space-2)">
    <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-xs)', fontWeight: 600, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 'var(--lk-text-lg)', fontWeight: 700, color: color ? `var(--lk-${color}-600)` : 'var(--lk-text-primary)' }}>{value}</div>
  </Surface>
);

const UnderwriterApplicationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [notes, setNotes] = useState('');
  
  const app = mockApplication;
  
  const satisfiedConditions = app.conditions.filter((c) => c.status === 'satisfied').length;
  const totalConditions = app.conditions.length;
  const conditionProgress = (satisfiedConditions / totalConditions) * 100;

  return (
    <div>
      {/* Application Header Bar */}
      <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)' }}>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/uw/dashboard')} />
            <div>
              <h4 style={{ fontFamily: 'monospace', fontWeight: 700, margin: '0 0 4px' }}>{app.anonymous_id}</h4>
              <div style={{ display: 'flex', gap: 'var(--lk-space-2)' }}>
                <Badge variant="primary">{app.selected_product.short_name}</Badge>
                <Badge variant="primary">In Progress</Badge>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--lk-space-2)' }}>
            <Button variant="danger">Needs Work</Button>
            <Button variant="secondary">Hold</Button>
            <Button variant="primary">Clear for Marketplace</Button>
          </div>
        </div>
      </Surface>

      {/* 3-Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 'var(--lk-space-4)' }}>
        {/* LEFT COLUMN - Score & Conditions */}
        <div>
          {/* Readiness Score */}
          <Surface padding="var(--lk-space-4)" style={{ textAlign: 'center', marginBottom: 'var(--lk-space-4)' }}>
            <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--lk-space-2)' }}>READINESS SCORE</div>
            <div style={{ fontSize: '3rem', fontWeight: 700, color: app.readiness_score >= 70 ? 'var(--lk-success-500)' : 'var(--lk-warning-500)', marginBottom: 'var(--lk-space-2)' }}>
              {app.readiness_score}
            </div>
            <div style={{ height: 6, borderRadius: 3, backgroundColor: 'var(--lk-gray-100)' }}>
              <div style={{ height: '100%', width: `${app.readiness_score}%`, borderRadius: 3, backgroundColor: app.readiness_score >= 70 ? 'var(--lk-success-500)' : 'var(--lk-warning-500)', transition: 'width 0.6s ease' }} />
            </div>
          </Surface>

          {/* Conditions */}
          <Surface padding="var(--lk-space-4)">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--lk-space-2)' }}>
              <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-xs)', fontWeight: 600, textTransform: 'uppercase' }}>CONDITIONS</div>
              <Badge variant={satisfiedConditions === totalConditions ? 'success' : 'warning'}>
                {satisfiedConditions}/{totalConditions}
              </Badge>
            </div>
            <div style={{ height: 4, borderRadius: 2, backgroundColor: 'var(--lk-gray-100)', marginBottom: 'var(--lk-space-3)' }}>
              <div style={{ height: '100%', width: `${conditionProgress}%`, borderRadius: 2, backgroundColor: 'var(--lk-primary-500)', transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
              {app.conditions.map((condition) => (
                <div key={condition.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--lk-text-sm)' }}>{condition.title}</span>
                  <Badge variant={getConditionStatusColor(condition.status)}>
                    {condition.status.replace('_', ' ')}
                  </Badge>
                </div>
              ))}
            </div>
            <Button variant="secondary" icon={Plus} size="sm" style={{ width: '100%', marginTop: 'var(--lk-space-3)' }}>
              Add Condition
            </Button>
          </Surface>
        </div>

        {/* CENTER COLUMN - Loan Details */}
        <div>
          <div style={{ marginBottom: 'var(--lk-space-3)' }}>
            <Tabs
              tabs={[
                { id: 0, label: 'Loan Overview' },
                { id: 1, label: `Documents (${app.documents.length})` },
              ]}
              activeTab={tab}
              onChange={setTab}
            />
          </div>

          {tab === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
              {/* Borrower & Property */}
              <Grid cols={2} gap="var(--lk-space-3)">
                <Surface padding="var(--lk-space-4)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
                    <User size={16} color="var(--lk-primary-500)" />
                    <span style={{ fontWeight: 600 }}>Borrower</span>
                  </div>
                  <div style={{ fontWeight: 600 }}>{app.applicant.first_name} {app.applicant.last_name}</div>
                  <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{app.applicant.email}</div>
                  <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{app.applicant.phone}</div>
                </Surface>
                <Surface padding="var(--lk-space-4)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
                    <Home size={16} color="var(--lk-primary-500)" />
                    <span style={{ fontWeight: 600 }}>Property</span>
                  </div>
                  <div style={{ fontWeight: 600 }}>{app.property.type} • {app.property.use}</div>
                  <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{app.property.address}</div>
                </Surface>
              </Grid>

              {/* Loan Metrics */}
              <Surface padding="var(--lk-space-4)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
                  <DollarSign size={16} color="var(--lk-primary-500)" />
                  <span style={{ fontWeight: 600 }}>Loan Details</span>
                </div>
                <Grid cols={4} gap="var(--lk-space-3)">
                  <MetricCard label="Purchase Price" value={`$${app.property.purchase_price.toLocaleString()}`} />
                  <MetricCard label="Down Payment" value={`$${app.property.down_payment.toLocaleString()}`} />
                  <MetricCard label="Loan Amount" value={`$${app.property.loan_amount.toLocaleString()}`} color="primary" />
                  <MetricCard label="LTV Ratio" value={`${app.property.ltv_ratio}%`} />
                </Grid>
              </Surface>

              {/* Credit */}
              <Surface padding="var(--lk-space-4)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
                  <CreditCard size={16} color="var(--lk-primary-500)" />
                  <span style={{ fontWeight: 600 }}>Credit Summary</span>
                </div>
                <Grid cols={4} gap="var(--lk-space-3)">
                  <Surface padding="var(--lk-space-2)" style={{ background: 'var(--lk-success-500)', color: 'white', textAlign: 'center', borderRadius: 'var(--lk-radius-md)' }}>
                    <div style={{ fontSize: 'var(--lk-text-xs)', opacity: 0.8 }}>FICO</div>
                    <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700 }}>{app.credit.score}</div>
                  </Surface>
                  <MetricCard label="Trade Lines" value={app.credit.accounts} />
                  <MetricCard label="Derogatory" value={app.credit.derogatory} color="success" />
                  <MetricCard label="Inquiries" value={app.credit.inquiries} />
                </Grid>
              </Surface>

              {/* Income */}
              <Surface padding="var(--lk-space-4)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
                  <Briefcase size={16} color="var(--lk-primary-500)" />
                  <span style={{ fontWeight: 600 }}>Income</span>
                </div>
                <table className="lk-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Employer</th>
                      <th style={{ textAlign: 'right' }}>Monthly</th>
                      <th style={{ textAlign: 'center' }}>Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {app.income.sources.map((source, idx) => (
                      <tr key={idx}>
                        <td>{source.type}</td>
                        <td>{source.employer}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>${source.amount.toLocaleString()}</td>
                        <td style={{ textAlign: 'center' }}>
                          {source.verified ? 
                            <CircleCheck size={16} color="var(--lk-success-500)" /> : 
                            <Clock size={16} color="var(--lk-warning-500)" />
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <hr style={{ border: 'none', borderTop: '1px solid var(--lk-border-subtle)', margin: 'var(--lk-space-3) 0' }} />
                <Grid cols={3} gap="var(--lk-space-3)">
                  <MetricCard label="Monthly Income" value={`$${app.income.monthly.toLocaleString()}`} color="primary" />
                  <MetricCard label="Annual Income" value={`$${app.income.annual.toLocaleString()}`} />
                  <MetricCard label="DTI Ratio" value={`${app.income.dti_ratio}%`} color={app.income.dti_ratio <= 43 ? 'success' : 'warning'} />
                </Grid>
              </Surface>

              {/* Assets */}
              <Surface padding="var(--lk-space-4)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
                  <Wallet size={16} color="var(--lk-primary-500)" />
                  <span style={{ fontWeight: 600 }}>Assets</span>
                </div>
                <table className="lk-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Institution</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                      <th style={{ textAlign: 'center' }}>Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {app.assets.items.map((asset, idx) => (
                      <tr key={idx}>
                        <td>{asset.type}</td>
                        <td>{asset.institution}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>${asset.amount.toLocaleString()}</td>
                        <td style={{ textAlign: 'center' }}>
                          {asset.verified ? 
                            <CircleCheck size={16} color="var(--lk-success-500)" /> : 
                            <Clock size={16} color="var(--lk-warning-500)" />
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <hr style={{ border: 'none', borderTop: '1px solid var(--lk-border-subtle)', margin: 'var(--lk-space-3) 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>Total Assets</span>
                  <span style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>${app.assets.total.toLocaleString()}</span>
                </div>
              </Surface>
            </div>
          )}

          {tab === 1 && (
            <Surface padding="0" style={{ overflow: 'hidden' }}>
              <table className="lk-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Category</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {app.documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                          {getDocStatusIcon(doc.status)}
                          <span style={{ fontWeight: 500 }}>{doc.name}</span>
                        </div>
                      </td>
                      <td>
                        <Badge variant="neutral">{doc.category}</Badge>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Badge variant={doc.status === 'verified' ? 'success' : doc.status === 'in_review' ? 'primary' : 'warning'}>
                          {doc.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          <Button size="sm" variant="secondary">View</Button>
                          {doc.status !== 'verified' && (
                            <Button size="sm" variant="primary">Accept</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Surface>
          )}
        </div>

        {/* RIGHT COLUMN - Notes */}
        <div>
          {/* Checklist */}
          <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
            <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Review Checklist</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
              {[
                { label: 'Credit verified', done: true },
                { label: 'Income calculated', done: true },
                { label: 'Assets verified', done: false },
                { label: 'DTI within limits', done: true },
                { label: 'All conditions cleared', done: false },
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                  {item.done ? (
                    <CircleCheck size={16} color="var(--lk-success-500)" />
                  ) : (
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid var(--lk-border-default)' }} />
                  )}
                  <span style={{ fontSize: 'var(--lk-text-sm)', color: item.done ? 'var(--lk-text-primary)' : 'var(--lk-text-tertiary)' }}>{item.label}</span>
                </div>
              ))}
            </div>
          </Surface>

          {/* Notes */}
          <Surface padding="var(--lk-space-4)">
            <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Underwriter Notes</div>
            <Textarea
              rows={4}
              placeholder="Add notes about this application..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button variant="primary" icon={Send} style={{ width: '100%', marginTop: 'var(--lk-space-3)' }}>
              Save Notes
            </Button>
          </Surface>
        </div>
      </div>

      <FloatingOwenAssistant 
        context="underwriter-application-view"
        contextData={{
          application: mockApplication,
          activeTab: tab,
          readinessScore: mockApplication.readiness_score,
        }}
      />
      <FloatingKnowledge 
        customContent={{
          label: 'Loan Review',
          title: 'Application review checklist',
          body: 'Comprehensive review ensures accurate underwriting decisions.',
          bullets: [
            'Verify all income sources and documentation',
            'Review credit report for accuracy and issues',
            'Calculate DTI ratio and compare to guidelines',
            'Assess property value and appraisal details',
            'Document all conditions clearly and specifically',
          ],
          resources: [
            { label: 'Underwriting guidelines', href: '#' },
            { label: 'Condition templates', href: '#' },
          ],
        }}
      />
    </div>
  );
};

export default UnderwriterApplicationView;
